// Helpers for calling the Zoom Video SDK from UI code without letting its failures escape
// as unhandled rejections. Pure, so they run under plain Node in tests.

// Runs one SDK call and reports a failure instead of throwing. The call is wrapped in an
// async function so a synchronous throw is caught the same way as a rejected promise.
// Resolves true when the call succeeded.
export const guardSdkCall = async (
   operation: string,
   call: () => unknown,
   report: (operation: string, error: unknown) => void,
): Promise<boolean> => {
   try {
      await call();
      return true;
   } catch (error) {
      report(operation, error);
      return false;
   }
};

export interface LocalMediaStream {
   isCapturingVideo: () => boolean;
   isAudioMuted: () => boolean;
}

export interface LocalMediaState {
   video: boolean;
   audio: boolean;
}

// What the SDK says the local camera and microphone are doing right now. After a toggle
// that failed halfway the UI flags can disagree with the SDK; this is the source of
// truth to resync them from. The stream comes from a getter so that a throwing
// `getMediaStream()` is caught here too. Returns null when the SDK cannot answer.
export const readLocalMediaState = (
   getStream: () => LocalMediaStream | null | undefined,
): LocalMediaState | null => {
   try {
      const stream = getStream();
      if (!stream) return null;
      return { video: stream.isCapturingVideo(), audio: !stream.isAudioMuted() };
   } catch {
      return null;
   }
};

export interface SdkToggle {
   operation: string;
   // True while a previous run of the same toggle is still in flight.
   isBusy?: () => boolean;
   setBusy?: (busy: boolean) => void;
   run: () => unknown;
   // Re-reads the SDK into the UI flags once the run is over, whether or not it failed.
   resync: () => void;
   report: (operation: string, error: unknown) => void;
}

// A mute/camera toggle: one run at a time, failures reported rather than thrown, the UI
// flags resynced from the SDK afterwards, and the busy flag cleared even if the resync
// itself throws.
export const runSdkToggle = async (toggle: SdkToggle): Promise<void> => {
   if (toggle.isBusy?.()) return;
   toggle.setBusy?.(true);
   try {
      await toggle.run();
   } catch (error) {
      toggle.report(toggle.operation, error);
   } finally {
      try {
         toggle.resync();
      } catch (error) {
         toggle.report(`${toggle.operation}Resync`, error);
      }
      toggle.setBusy?.(false);
   }
};
